import React, { useContext, useState } from 'react';
import { View, FlatList, StyleSheet, Alert, Platform, TouchableOpacity } from 'react-native';
import { Text, FAB, Card, Title, Paragraph, Chip, IconButton, Portal, Dialog, Button } from 'react-native-paper';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { FirebaseTripsContext } from '../context/FirebaseTripsContext';
import { RootStackParamList } from '../navigation/RootNavigator';
import { formatDate } from '../utils/dateUtils';

type NavigationProp = NativeStackNavigationProp<RootStackParamList, 'Main'>;

export default function TripsListScreen() {
  const navigation = useNavigation<NavigationProp>();
  const tripsContext = useContext(FirebaseTripsContext);
  
  // Debug: Add visible test content
  console.log('TripsListScreen rendering');
  
  if (!tripsContext) {
    return (
      <View style={styles.centered}>
        <Text>Loading...</Text>
      </View>
    );
  }

  const { allTrips, isLoading, sharedTrips, deleteTrip } = tripsContext;
  const [deletingTripId, setDeletingTripId] = useState<string | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{ visible: boolean; tripId: string; tripName: string }>({
    visible: false,
    tripId: '',
    tripName: ''
  });

  const handleDeleteTrip = async (tripId: string, tripName: string) => {
    console.log('Delete button clicked for trip:', tripId, tripName);
    
    if (Platform.OS === 'web') {
      // Use dialog for web
      setDeleteDialog({ visible: true, tripId, tripName });
    } else {
      // Use native Alert for mobile
      Alert.alert(
        'Delete Trip',
        `Are you sure you want to delete "${tripName}"?`,
        [
          {
            text: 'Cancel',
            style: 'cancel'
          },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: () => performDelete(tripId)
          }
        ]
      );
    }
  };

  const performDelete = async (tripId: string) => {
    console.log('Deleting trip:', tripId);
    try {
      setDeletingTripId(tripId);
      await deleteTrip(tripId);
      console.log('Trip deleted successfully');
    } catch (error) {
      console.error('Error deleting trip:', error);
      if (Platform.OS === 'web') {
        // Show error in console for web
        console.error('Failed to delete trip');
      } else {
        Alert.alert('Error', 'Failed to delete trip');
      }
    } finally {
      setDeletingTripId(null);
      setDeleteDialog({ visible: false, tripId: '', tripName: '' });
    }
  };

  const renderTrip = ({ item }: { item: typeof allTrips[0] }) => {
    const isShared = sharedTrips.some(trip => trip.id === item.id);
    const isDeleting = deletingTripId === item.id;
    
    return (
      <Card
        style={[styles.card, isDeleting && styles.deletingCard]}
      >
        <TouchableOpacity
          onPress={() => {
            if (!isDeleting) {
              navigation.navigate('TripDetail', { tripId: item.id });
            }
          }}
          activeOpacity={0.7}
        >
          <Card.Cover source={{ uri: item.coverImage || 'https://picsum.photos/700' }} />
          <Card.Content style={styles.cardContent}>
            <View style={styles.titleRow}>
              <View style={styles.titleContainer}>
                <Title>{item.name}</Title>
                {isShared && <Chip mode="outlined" compact style={styles.sharedChip}>Shared</Chip>}
              </View>
              <IconButton
                icon="delete"
                size={20}
                onPress={() => {
                  handleDeleteTrip(item.id, item.name);
                }}
                disabled={isDeleting}
              />
            </View>
            <Paragraph>{item.destination}</Paragraph>
            <Text style={styles.dates}>
              {formatDate(item.startDate)} - {formatDate(item.endDate)}
            </Text>
          </Card.Content>
        </TouchableOpacity>
      </Card>
    );
  };

  if (isLoading) {
    return (
      <View style={styles.centered}>
        <Text>Loading trips...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {allTrips.length === 0 ? (
        <View style={styles.centered}>
          <Text style={styles.emptyText}>No trips yet!</Text>
          <Text>Tap the + button to add your first trip</Text>
        </View>
      ) : (
        <FlatList
          data={allTrips}
          renderItem={renderTrip}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.list}
          refreshing={isLoading}
          onRefresh={() => tripsContext.refreshTrips()}
        />
      )}
      <FAB
        style={styles.fab}
        icon="plus"
        onPress={() => navigation.navigate('AddTrip' as any)}
      />
      
      <Portal>
        <Dialog visible={deleteDialog.visible} onDismiss={() => setDeleteDialog({ visible: false, tripId: '', tripName: '' })}>
          <Dialog.Title>Delete Trip</Dialog.Title>
          <Dialog.Content>
            <Text>Are you sure you want to delete "{deleteDialog.tripName}"?</Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setDeleteDialog({ visible: false, tripId: '', tripName: '' })}>Cancel</Button>
            <Button onPress={() => performDelete(deleteDialog.tripId)} textColor="red">Delete</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  list: {
    padding: 16,
  },
  card: {
    marginBottom: 16,
  },
  cardContent: {
    paddingTop: 16,
  },
  dates: {
    marginTop: 8,
    color: '#666',
  },
  emptyText: {
    fontSize: 18,
    marginBottom: 8,
  },
  fab: {
    position: 'absolute',
    margin: 16,
    right: 0,
    bottom: 0,
  },
  titleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  titleContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  sharedChip: {
    marginLeft: 8,
  },
  deletingCard: {
    opacity: 0.5,
  },
});